import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Button } from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetCoupon, useUpdateCoupon } from "../../api/hooks/useCoupon";
import { showError, showSuccess } from "../../helpers/toast";
import { VALUE_TYPE, DISCOUNT_OPTION } from "../../constants/discount";
import dayjs from "dayjs";

const ViewDiscount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const couponId = location?.state?.couponId;

  const { data: couponData, isLoading: isLoadingCoupon, error: couponError } = useGetCoupon(
    couponId || '',
    { enabled: !!couponId }
  );

  const coupon = couponData?.data;

  // Update coupon status mutation
  const { mutate: updateCouponStatus, isPending: isUpdatingStatus } = useUpdateCoupon({
    onSuccess: (response) => {
      showSuccess(response.message || "Coupon status updated successfully");
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update coupon status");
    },
  });

  useEffect(() => {
    if (couponError) {
      showError((couponError as any)?.response?.data?.message || "Failed to load coupon details");
    }
  }, [couponError]);

  useEffect(() => {
    if (!couponId && !isLoadingCoupon) {
      showError("No coupon selected. Redirecting to coupon list.");
      navigate("/discounts");
    }
  }, [couponId, isLoadingCoupon, navigate]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const handleStatusToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (coupon) {
      updateCouponStatus({
        id: coupon._id,
        data: {
          name: coupon.name,
          value: coupon.value,
          valueType: coupon.valueType,
          usageLimit: coupon.usageLimit,
          expiryDate: coupon.expiryDate || "",
          option: coupon.option,
          newUserOnly: coupon.newUserOnly,
          status: event.target.checked,
        },
      });
    }
  };

  if (isLoadingCoupon) {
    return (
      <>
        <div className="main_title">
          <h1>Discount and Coupon Detail</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
            <button onClick={() => navigate("/discounts")}>Discounts and Coupons</button> - View Discount and Coupon
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!coupon) {
    return (
      <>
        <div className="main_title">
          <h1>Discount and Coupon Detail</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
            <button onClick={() => navigate("/discounts")}>Discounts and Coupons</button> - View Discount and Coupon
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <p>Coupon not found.</p>
        </Box>
      </>
    );
  }

  const discountDisplay = coupon.valueType === VALUE_TYPE.FLAT
    ? `$${coupon.value}`
    : `${coupon.value}%`;

  const discountOptionDisplay = coupon.option === DISCOUNT_OPTION.ONE_TIME
    ? "One Time"
    : "Recurring";

  return (
    <>
      <div className="main_title">
        <h1>Discount and Coupon Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          <button onClick={() => navigate("/discounts")}>Discounts and Coupons</button> - View Discount and Coupon
        </p>
      </div>

      <div className="cards">
        <div className="view_page">
          <div className="table_header">
            <h2>Coupon Details</h2>
            <div className="right_s">
              <Button
                className="btn btn_primary sm"
                onClick={() => navigate("/discounts/add", { state: { couponId: coupon._id } })}
                disabled={isUpdatingStatus}
              >
                Edit Coupon
              </Button>
            </div>
          </div>

          <div className="gap_p">
            <div className="w_33">
              <h6>Offer/Discount Name</h6>
              <p>{coupon.name || "-"}</p>
            </div>

            <div className="w_33">
              <h6>Coupon Code</h6>
              <p>
                {coupon.code ? (
                  <span className="status_btn c_success">{coupon.code}</span>
                ) : (
                  coupon.couponCode ? coupon.couponCode : "N/A"
                )}
              </p>
            </div>

            <div className="w_33">
              <h6>Discount</h6>
              <p>{discountDisplay}</p>
            </div>

            <div className="w_33">
              <h6>Discount Type</h6>
              <p>{coupon.valueType === VALUE_TYPE.FLAT ? "Flat ($)" : "Percentage (%)"}</p>
            </div>

            <div className="w_33">
              <h6>Discount Option</h6>
              <p>{discountOptionDisplay}</p>
            </div>

            <div className="w_33">
              <h6>Usage Limit</h6>
              <p>{coupon.usageLimit || "-"}</p>
            </div>

            <div className="w_33">
              <h6>Expiry Date</h6>
              <p>{coupon.expiryDate ? formatDate(coupon.expiryDate) : "No expiry"}</p>
            </div>

            <div className="w_33">
              <h6>New User Only</h6>
              <p>
                {coupon.newUserOnly ? (
                  <span className="status_btn c_success">Yes</span>
                ) : (
                  <span className="status_btn c_warning">No</span>
                )}
              </p>
            </div>

            <div className="w_33">
              <h6>Created Date</h6>
              <p>{formatDate(coupon.createdAt)}</p>
            </div>

            <div className="w_33">
              <h6>Status</h6>
              <p>
                <SwitchToggle
                  checked={coupon.status ?? true}
                  onChange={handleStatusToggle}
                  disabled={isUpdatingStatus}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewDiscount;
